import { NewPageWrapper, RichTable, useTitle, getApiConfiguration } from '../../common';
import { CellProps, TenantsComponent } from './Tenants.types';
import { Flexbox, Link, Table, Typography } from '@hiiretail/synergy-ui';
import { useContext, useEffect, useMemo, useState } from 'react';
import { CreateTenantModal } from './ui/modals/CreateTenantModal/CreateTenantModal';
import { useGetTenants } from './hooks/useGetTenants';
import { TenantDto, TenantsApi } from '../../api-client';
import { TableData } from '@hiiretail/synergy-ui/dist/types/components/surfaces/Table/Table.types';
import { useCreateTenant } from './hooks/useCreateTenant';
import { NotificationsContext } from '../../common/notifications/Notifications';
import { UpdateTenantModal } from './ui/modals/UpdateTenantModal/UpdateTenantModal';
import { useUpdateTenant } from './hooks/useUpdateTenant';
import { RowActions } from '../../common/components/RowActions/RowActions';
import { ErrorResponse } from '../../common/types/errorRespose';
import { useSearch } from '../../common/hooks/useSearch/useSearch';
import { FuseOptions } from 'fuse.js';
import { useBreadcrumbsActions } from '../../common/components/Breadcrumbs/context/useBreadcrumbsActions';
import { CreateButton } from '../../common/components/Breadcrumbs/buttons/CreateButton/CreateButton';
import { RefreshButton } from '../../common/components/Breadcrumbs/buttons/RefreshButton/RefreshButton';
import { Status } from '../../common/components/Status/Status';
import { CopyToClipboard } from '../../common/components/CopyToClipboard/CopyToClipboard';
import { useQueryClient } from 'react-query';

export const Tenants: TenantsComponent = () => {
  useTitle('Tenants');
  const [isCreateTenantModalOpen, setIsCreateTenantModalOpen] = useState(false);
  const [isUpdateTenantModalOpen, setIsUpdateTenantModalOpen] = useState(false);
  const [tenantToUpdate, setTenantToUpdate] = useState<TenantDto | null>(null);

  const queryClient = useQueryClient();
  const tenantsApi = new TenantsApi(getApiConfiguration());
  const { showNotification } = useContext(NotificationsContext);

  const { updateActions } = useBreadcrumbsActions();

  const {
    data: tenants,
    isFetching: isLoading,
    error: fetchError,
    refetch: refetchTenants,
  } = useGetTenants(tenantsApi, () => void 0);

  const {
    mutate: createTenant,
    isLoading: isCreating,
    error: createError,
  } = useCreateTenant(
    tenantsApi,
    () => void 0,
    ({ data: { name } }) => {
      showNotification(`Tenant ${name} created successfully`);
      toggleCreateTenantDialogOpen();
      queryClient.invalidateQueries('tenants').then();
    },
  );

  const {
    mutate: updateTenant,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateTenant(
    tenantsApi,
    () => void 0,
    () => {
      showNotification(`Tenant information updated successfully`);
      toggleUpdateTenantDialogOpen();
    },
  );

  useEffect(() => {
    updateActions(
      <>
        <Flexbox item>
          <RefreshButton onClick={refetchTenants} permission="iam.tenant.list" />
        </Flexbox>
        <Flexbox item>
          <CreateButton
            title="New tenant"
            onClick={toggleCreateTenantDialogOpen}
            permission="iam.tenant.create"
            disabled={isLoading || isCreating || isUpdating}
          />
        </Flexbox>
      </>,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, isCreating, isUpdating]);

  const searchOptions: FuseOptions<TenantDto> = useMemo(
    () => ({
      keys: ['name', 'alias', 'id'],
      threshold: 0.1,
      caseSensitive: false,
      findAllMatches: true,
    }),
    [],
  );

  const { filteredData: filteredTenants, onSearchChange } = useSearch(
    tenants as TenantDto[],
    searchOptions,
  );

  const tableColumns = useMemo(
    () => [
      { Header: 'Name', accessor: 'name', width: 200 },
      // { Header: 'Alias', accessor: 'alias', width: 150 },
      {
        Header: 'URL',
        width: 300,
        Cell: ({ row: { original } }: CellProps) => (
          <Link to={`https://${original.alias}.hiiretail.com`} external target="_blank">
            <Typography variant="body2">{`https://${original.alias}.hiiretail.com`}</Typography>
          </Link>
        ),
      },
      {
        Header: 'Id',
        accessor: 'id',
        width: 250,
        Cell: ({ row: { original } }: CellProps) => (
          <CopyToClipboard text={original.id} variant="body2" ellipsis showTooltip />
        ),
      },
      {
        Header: 'Staff ID',
        accessor: 'staffTenantId',
        width: 200,
        Cell: ({ row: { original } }: CellProps) => (
          <CopyToClipboard text={original.staffTenantId} variant="body2" ellipsis showTooltip />
        ),
      },
      {
        Header: 'Shoppers ID',
        accessor: 'shopperTenantId',
        width: 200,
        Cell: ({ row: { original } }: CellProps) => (
          <CopyToClipboard text={original.shopperTenantId} variant="body2" ellipsis showTooltip />
        ),
      },

      {
        Header: 'Status',
        accessor: 'status',
        width: 100,
        Cell: ({ row: { original } }: CellProps) => (
          <Status status={original.status} variant="caption" />
        ),
      },
      { Header: 'Administrator email', accessor: 'adminUser', width: 200 },
      {
        Header: 'Actions',
        accessor: (row: TenantDto) => `actions-${row.id}`,
        width: 100,
        Cell: ({ row: { original } }: CellProps) => (
          <RowActions
            original={original}
            setItemToUpdate={setTenantToUpdate}
            toggleUpdateItemModal={toggleUpdateTenantDialogOpen}
            updatePermission="iam.tenant.update"
          />
        ),
        align: Table.CONSTANTS.COLUMN_ALIGNMENT.RIGHT_ALIGNMENT,
      },
    ],
    [],
  );

  const toggleCreateTenantDialogOpen = () => {
    setIsCreateTenantModalOpen((prevVal) => !prevVal);
  };

  const toggleUpdateTenantDialogOpen = () => {
    setIsUpdateTenantModalOpen((prevVal) => !prevVal);
  };

  return (
    <NewPageWrapper>
      <CreateTenantModal
        onClose={toggleCreateTenantDialogOpen}
        isOpen={isCreateTenantModalOpen}
        error={createError}
        isCreating={isCreating}
        createTenant={createTenant}
      />
      {tenantToUpdate && (
        <UpdateTenantModal
          onClose={toggleUpdateTenantDialogOpen}
          isOpen={isUpdateTenantModalOpen}
          isUpdating={isUpdating}
          error={updateError}
          updateTenant={updateTenant}
          tenant={tenantToUpdate}
        />
      )}
      <RichTable
        id="tenants-table"
        title="Browse tenants"
        columns={tableColumns}
        data={filteredTenants as unknown as TableData[]}
        refetch={refetchTenants}
        fetchError={fetchError as ErrorResponse | null}
        isLoading={isLoading}
        errorSubject="tenants"
        searchFieldLabel="Filter by id, name or alias"
        onSearchChange={onSearchChange}
        permissions={{
          create: 'iam.tenant.create',
          list: 'iam.tenant.list',
        }}
      />
    </NewPageWrapper>
  );
};
